import {useEffect} from 'react'
import type {BannerProps} from '@primer/react'
import {useAddBanner, useDismissBanner, type InitialBanner} from '@github-ui/banner-stack'
import type {BillingCriticalBannerPayload} from '../../routes/workflows-layout-route'

export const BILLING_CRITICAL_BANNER_ID = 'billing-critical-banner'

type BillingCriticalBanner = InitialBanner & BannerProps & {'data-test-selector'?: string}

/** Builds the billing-critical (trust-tier usage limit) banner from its route payload. */
export function getBillingCriticalBanner({isAdmin}: BillingCriticalBannerPayload): BillingCriticalBanner {
  return {
    id: BILLING_CRITICAL_BANNER_ID,
    title: 'GitHub Actions usage limit',
    hideTitle: true,
    description: (
      <p data-test-selector="warning-description">
        Your account has reached its current usage limit for <b>Actions</b>.{' '}
        <span>
          {isAdmin
            ? 'Get in touch with our customer support team to continue.'
            : 'Please contact your admin to continue.'}
        </span>
      </p>
    ),
    variant: 'critical',
    'data-test-selector': BILLING_CRITICAL_BANNER_ID,
    // Sourced from the layout-level route payload, which doesn't refetch on leaf-route soft navigation.
    persistOnNavigation: true,
  }
}

/** Keeps the billing-critical notice synchronized with the active workflows-layout route payload. */
export function useBillingCriticalBanner({data}: {data?: BillingCriticalBannerPayload | null}) {
  const addBanner = useAddBanner()
  const dismissBanner = useDismissBanner()

  useEffect(() => {
    if (!data) {
      dismissBanner(BILLING_CRITICAL_BANNER_ID)
      return
    }

    const id = addBanner(getBillingCriticalBanner(data))

    return () => {
      dismissBanner(id)
    }
  }, [data, addBanner, dismissBanner])
}
